import { FC, useState, useMemo, useEffect } from "react";
import {
  Modal,
  Row,
  Col,
  Tag,
  Divider,
  Input,
  Button,
  Space,
  DatePicker,
  Radio,
  Select,
} from "antd";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import dayjs from "dayjs";
import { IPatientCaseTypesState } from "../../../interfaces/ICase";

type ClaimIdValidationModalProps = {
  open: boolean;
  onCancel: () => void;
  validationRes: any;
  onUpdate?: (updatedG7: any) => void | Promise<void>;
  totalCases: Array<IPatientCaseTypesState>;
};

const ClaimIdValidationModal: FC<ClaimIdValidationModalProps> = ({
  open,
  onCancel,
  validationRes,
  onUpdate,
  totalCases,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const { patients, preAuth } = useSelector((state: RootState) => state);
  const dateFormat = "DD-MM-YYYY";

  // Local editable G7 state
  const [g7Data, setG7Data] = useState<any>(() => ({
    patientName: patients?.fetchBasicDetails?.patientResponse?.fullName,
    // age: patients?.fetchBasicDetails?.patientResponse?.age,
    cardId:
      preAuth?.portalData?.beneficiaryId ||
      patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
        ?.cardNumber,
    claimId:
      preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsReferralResponse
        ?.claimId ||
      preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsEmergencyResponse
        ?.claimId,
    admissionType: patients?.fetchBasicDetails?.visitInfoResponse?.visitType,
  }));

  useEffect(() => {
    setG7Data({
      ...g7Data,
      cardId:
        preAuth?.portalData?.beneficiaryId ||
        patients?.fetchBasicDetails?.referralInfoResponse?.echsReferralResponse
          ?.cardNumber,
      claimId:
        preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsReferralResponse
          ?.claimId ||
        preAuth?.portalData?.casePreAuthEchs?.casePreAuthEchsEmergencyResponse
          ?.claimId,
    });
  }, [preAuth?.portalData]);

  const handleChange = (field: string, value: any) => {
    if (field === "caseType") {
      let visitType = value
        ?.split("~")?.[1]
        ?.toUpperCase()
        ?.includes("EMERGENCY")
        ? "EMERGENCY"
        : value?.split("~")[1]?.toUpperCase()?.includes("OP") ||
            value?.split("~")[1]?.toUpperCase()?.includes("DIALYSIS")
          ? "OP"
          : "IP";

      setG7Data({
        ...g7Data,
        caseType: value?.split("~")?.[0],
        admissionType: visitType,
      });
    } else setG7Data({ ...g7Data, [field]: value });
  };

  const handleUpdate = async () => {
    if (onUpdate) await onUpdate(g7Data);
    setIsEditing(false);
  };

  // Detect discrepancy for any field
  const hasMismatch = useMemo(() => {
    const fields = [
      "patientName",
      // "age",
      "cardId",
      "claimId",
      "admissionType",
    ];

    return fields?.some((key) => {
      let portalValue = validationRes?.claimResponseDTO?.[key];
      let g7Value = g7Data?.[key];

      if (key === "admissionType") {
        if (
          g7Value?.toString()?.toLowerCase() === "emergency" ||
          g7Value?.toString()?.toLowerCase() === "ip"
        ) {
          g7Value = "IN";
        } else if (g7Value?.toString()?.toLowerCase() === "op") {
          g7Value = "OPD";
        }
      }

      if (key === "cardId") {
        portalValue = portalValue?.replace(/\D/g, "")?.slice(-12);
      }

      return (
        portalValue?.toString()?.toLowerCase()?.trim() !==
        g7Value?.toString()?.toLowerCase()?.trim()
      );
    });
  }, [validationRes, g7Data]);

  // Determine status
  // const derivedStatus = hasMismatch ? "DISCREPANCY" : validationRes?.status;
  const derivedStatus = isEditing
    ? "DISCREPANCY"
    : hasMismatch
      ? "DISCREPANCY"
      : validationRes?.status;

  const renderFieldRow = (label: string, fieldKey: string) => {
    const portalValue = validationRes?.claimResponseDTO?.[fieldKey];
    let g7Value = g7Data?.[fieldKey];

    let isMismatch;

    if (fieldKey === "admissionType") {
      if (
        g7Value?.toString()?.toLowerCase() === "emergency" ||
        g7Value?.toString()?.toLowerCase() === "ip"
      ) {
        isMismatch =
          portalValue?.toString()?.toLowerCase()?.trim() !==
          "IN"?.toString()?.toLowerCase()?.trim();
      } else if (g7Value?.toString()?.toLowerCase() === "op") {
        isMismatch =
          portalValue?.toString()?.toLowerCase()?.trim() !==
          "OPD"?.toString()?.toLowerCase()?.trim();
      } else {
        isMismatch =
          portalValue?.toString()?.toLowerCase()?.trim() !==
          g7Value?.toString()?.toLowerCase()?.trim();
      }
    }
    else if(fieldKey === "cardId") {
      isMismatch = portalValue?.toString()?.replace(/\D/g, "")?.slice(-12)?.toLowerCase()?.trim() !==
        g7Value?.toString()?.toLowerCase()?.trim();
    }
     else {
      isMismatch =
        portalValue?.toString()?.toLowerCase()?.trim() !==
        g7Value?.toString()?.toLowerCase()?.trim();
    }

    return (
      <Row
        gutter={16}
        style={{
          background: "#fff",
          padding: "8px 12px",
          borderRadius: 8,
          marginBottom: 10,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <Col span={8}>
          <strong>{label}</strong>
        </Col>

        <Col span={8}>
          <span>{portalValue || "-"}</span>
        </Col>

        <Col
          span={8}
          style={{
            color: isMismatch ? "#d92955ff" : "inherit",
            borderRadius: 6,
            padding: isEditing ? 0 : "6px 0",
            transition: "0.3s",
          }}
        >
          {isEditing ? (
            fieldKey === "registrationDate" ? (
              <DatePicker
                format={dateFormat}
                value={g7Value ? dayjs(g7Value, dateFormat, true) : null}
                onChange={(date, dateString) =>
                  handleChange(fieldKey, dateString)
                }
                style={{ width: "100%" }}
              />
            ) : fieldKey === "gender" ? (
              <Radio.Group
                name="gender"
                value={g7Value}
                onChange={(e) => handleChange(fieldKey, e.target.value)}
              >
                <Radio value="Male">MALE</Radio>
                <Radio value="Female">FEMALE</Radio>
              </Radio.Group>
            ) : fieldKey === "admissionType" ? (
              <Select
                showSearch
                placeholder="Select a case type"
                options={
                  totalCases
                    ? totalCases?.map((caseType) => ({
                        value: caseType?.id + "~" + caseType?.name,
                        label: caseType?.name,
                      }))
                    : []
                }
                defaultValue={
                  totalCases?.find(
                    (caseType) =>
                      caseType?.name?.toUpperCase() ===
                      patients?.fetchBasicDetails?.casesResponse?.caseTypeMetaDataName?.toUpperCase(),
                  )?.name
                }
                style={{ width: "100%" }}
                onChange={(caseType: any) => {
                  handleChange("caseType", caseType);
                }}
                allowClear
                // disabled={props?.isAddCase}
              />
            ) : (
              <Input
                value={g7Value}
                onChange={(e) => handleChange(fieldKey, e.target.value)}
              />
            )
          ) : (
            <span>{g7Value || "-"}</span>
          )}
        </Col>
      </Row>
    );
  };

  return (
    <Modal
      title={
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          Patient Registration Validation
        </div>
      }
      open={open}
      footer={null}
      onCancel={onCancel}
      width={850}
      bodyStyle={{ background: "#fafafa", padding: "20px 25px" }}
    >
      <Row style={{ marginBottom: 10 }}>
        <Col span={8}>
          <strong style={{ color: "rgb(0, 76, 143)" }}>Field</strong>
        </Col>
        <Col span={8}>
          <strong style={{ color: "rgb(0, 76, 143)" }}>Portal Details</strong>
        </Col>
        <Col span={8}>
          <strong style={{ color: "rgb(0, 76, 143)" }}>G7 Pro Details</strong>
        </Col>
      </Row>

      {/* Comparison */}
      {renderFieldRow("Patient Name", "patientName")}
      {/* {renderFieldRow("Age", "age")} */}
      {renderFieldRow("Card ID", "cardId")}
      {renderFieldRow("Claim ID", "claimId")}
      {/* {renderFieldRow("Registration Date", "registrationDate")} */}
      {/* {renderFieldRow("hospital", "hospital")} */}
      {renderFieldRow("Admission Type", "admissionType")}

      <Divider />

      <div style={{ textAlign: "center", margin: "20px 0" }}>
        <strong>Status: </strong>
        {derivedStatus === "VALIDATED" ? (
          <Tag color="success" style={{ fontSize: 15, padding: "6px 15px" }}>
            VALIDATED
          </Tag>
        ) : derivedStatus === "DISCREPANCY" ? (
          <Tag color="error" style={{ fontSize: 15, padding: "6px 15px" }}>
            MISMATCH
          </Tag>
        ) : (
          <Tag color="error" style={{ fontSize: 15, padding: "6px 15px" }}>
            NOT VALIDATED
          </Tag>
        )}
      </div>

      {!isEditing && validationRes?.status === "VALIDATED" && !hasMismatch ? (
        <></>
      ) : (
        <div style={{ textAlign: "center" }}>
          <Space>
            {!isEditing ? (
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                }}
                onClick={() => setIsEditing(true)}
              >
                Edit G7 Details
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  style={{
                    backgroundColor: "rgb(0, 76, 143)",
                    color: "#fff",
                    boxShadow: "0px 2px 8px 0px #00000040",
                  }}
                  onClick={handleUpdate}
                >
                  Update
                </Button>
                <Button onClick={() => setIsEditing(false)}>Cancel</Button>
              </>
            )}
          </Space>
        </div>
      )}
    </Modal>
  );
};

export default ClaimIdValidationModal;
